import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
    ArticleTemplate,
    cmsImageToOpenGraph,
    fetchCraftData,
    getMetaForStory,
    gtm,
    prefetchContentBlocks,
    PreviewData,
    SponsorsBanner,
    SponsorSet,
} from 'shared';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import ProfessionalsBreadcrumbs from '../../components/ProfessionalsBreadcrumbs';
import {
    SponsorsQuery,
    SponsorsQueryVariables,
    StoriesPathsDocument,
    StoriesPathsQuery,
    StoriesPathsQueryVariables,
    StoryDetailQuery,
    StoryDetailQueryVariables,
    useSponsorsQuery,
    useStoryDetailQuery,
    useStoryPathQuery,
} from '../../gql/cms';
import fetchStoryUri from '../../utils/fetchStoryUri';
import loadIntlMessages from '../../utils/loadIntlMessages';
import prefetchGlobals from '../../utils/prefetchGlobals';
import siteHandleByLocale, { getLocaleBySiteHandle } from '../../utils/siteHandleByLocale';
import { DEFAULT_REVALIDATION_TIME } from '../[...uri]';

export type StoryPage = Extract<StoryDetailQuery['entry'], { __typename?: 'professionalsStories_story_Entry' }>;

const Story: FC = () => {
    const { query, asPath } = useRouter();
    const intl = useIntl();
    const { locale } = intl;
    const { data: storyData } = useStoryDetailQuery({
        slug: query.slug,
        site: siteHandleByLocale[locale],
    });
    const storiesUri = useStoryPathQuery({
        site: siteHandleByLocale[locale],
    }).data.entry?.uri;
    const story = storyData?.entry.__typename === 'professionalsStories_story_Entry' && storyData.entry;
    const { title, headerImage, storyCategory, contentBlocks, sponsorBannerSet, authorName, postDate, introText } =
        story;
    const sponsorSet = sponsorBannerSet?.[0] as SponsorSet;

    useEffect(() => {
        gtm.pageView(asPath, {
            page_pillar: 'professionals',
            page_navigation: storiesUri,
            page_language: locale,
        });
    }, [storiesUri, asPath, locale]);

    return (
        <ArticleTemplate
            url={`${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/news/${query.slug}`}
            articleTitle={title}
            articleIntroText={introText}
            articleOpenGraphImages={cmsImageToOpenGraph(headerImage)}
            articleAuthor={authorName}
            articleCategory={storyCategory}
            articlePostDate={postDate}
            articleHeaderImage={headerImage[0]}
            articleMeta={getMetaForStory(story, intl)}
            contentBlocks={{
                contentBlocks,
            }}
            sponsorBanner={<SponsorsBanner mb={[10, null, null, undefined]} maxW="368px" sponsorSet={sponsorSet} />}
            breadcrumbs={
                <ProfessionalsBreadcrumbs
                    parentUri={storiesUri}
                    currentPage={{
                        title,
                        uri: asPath,
                    }}
                    onImage
                />
            }
        />
    );
};

export const getStaticPaths: GetStaticPaths = async () => {
    const data = await fetchCraftData<StoriesPathsQuery, StoriesPathsQueryVariables>({
        query: StoriesPathsDocument,
    });
    return {
        paths: data.professionalsStoriesEntries.map(entry => ({
            params: {
                slug: entry.slug,
                locale: getLocaleBySiteHandle(entry.siteHandle),
            },
        })),
        fallback: 'blocking',
    };
};

export const getStaticProps: GetStaticProps = async ({ params, locale, previewData }) => {
    const queryClient = new QueryClient();

    const variables: StoryDetailQueryVariables = {
        slug: params.slug,
        site: siteHandleByLocale[locale],
    };

    const data = await queryClient.fetchQuery(useStoryDetailQuery.getKey(variables), () =>
        fetchCraftData<StoryDetailQuery, StoryDetailQueryVariables>({
            query: useStoryDetailQuery.document,
            variables,
            previewData: previewData as PreviewData,
        })
    );

    if (data.entry?.__typename !== 'professionalsStories_story_Entry') {
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    await queryClient.prefetchQuery(useSponsorsQuery.getKey({}), () =>
        fetchCraftData<SponsorsQuery, SponsorsQueryVariables>({
            query: useSponsorsQuery.document,
            variables: {},
            previewData: previewData as PreviewData,
        })
    );

    await prefetchContentBlocks(queryClient, data.entry.contentBlocks, locale);
    const storiesUri = await fetchStoryUri(queryClient, locale);
    await prefetchGlobals(queryClient, locale, { pathType: 'PARENT_URI', uri: storiesUri });

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default Story;
